import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { recordPageVisit } from "@/lib/api";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";
import { 
  ClipboardList, 
  Heart, 
  Brain, 
  Activity, 
  Wind, 
  Droplets, 
  Zap,
  Copy,
  CheckCircle,
  LogIn
} from "lucide-react";

const DISEASES = {
  cancer: { name: "Cancer", icon: Activity, color: "text-rose-500" },
  heart_failure: { name: "Heart Failure / CHF", icon: Heart, color: "text-red-500" },
  dementia: { name: "Dementia / Alzheimer's", icon: Brain, color: "text-purple-500" },
  copd: { name: "COPD / Lung Disease", icon: Wind, color: "text-blue-500" },
  kidney_failure: { name: "Kidney / Renal Failure", icon: Droplets, color: "text-amber-500" },
  als: { name: "ALS / Neurological", icon: Zap, color: "text-teal-500" }
};

const VISIT_QUESTIONS = {
  Pain: [
    "On a scale of 0-10, how would you rate the pain today?",
    "Where exactly is the pain located?",
    "Is the current pain medication providing relief?",
    "How long does pain relief last after taking medication?",
    "Are there activities that make the pain worse or better?"
  ],
  "Breathing Difficulties": [
    "Is the shortness of breath worse at certain times of day?",
    "Does the oxygen seem to be helping?",
    "Are there positions that make breathing easier?",
    "Is anxiety making the breathing worse?",
    "Should we consider adjusting the oxygen flow rate?"
  ],
  Nausea: [
    "When does the nausea usually occur?",
    "Is the anti-nausea medication working?",
    "Are certain smells or foods triggering it?",
    "How much fluid is being kept down?",
    "Should we try a different anti-nausea approach?"
  ],
  Restlessness: [
    "When did the restlessness start?",
    "Is there pain or discomfort causing it?",
    "Does anything seem to calm them?",
    "Could the bladder be full?",
    "Should we consider medication for comfort?"
  ],
  "Appetite Changes": [
    "What foods or drinks are appealing right now?",
    "Is there any difficulty swallowing?",
    "Are they able to take medications by mouth?",
    "Would smaller, more frequent offerings help?",
    "Is mouth care helping with comfort?"
  ],
  Anxiety: [
    "What seems to be causing the anxiety?",
    "Are there specific fears we can address?",
    "Does presence and reassurance help?",
    "Would relaxation techniques or music help?",
    "Should we discuss medication options for anxiety?"
  ],
  General: [
    "Are there any new symptoms or concerns since the last visit?",
    "How is the patient's overall comfort level?",
    "Are the current medications working well?",
    "Does the family have any questions or concerns?",
    "Are there any upcoming events or needs we should plan for?"
  ]
};

export default function VisitPrep() {
  const { user } = useAuth();
  const [selectedDisease, setSelectedDisease] = useState("");
  const [copiedSection, setCopiedSection] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    recordPageVisit("visit-prep").catch(console.error);
  }, []);

  const copyQuestions = (section, questions) => {
    const text = `${section}:\n${questions.map((q, i) => `${i + 1}. ${q}`).join('\n')}`;
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    toast.success("Questions copied to clipboard");
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const DiseaseIcon = selectedDisease ? DISEASES[selectedDisease]?.icon : Activity;

  return (
    <div className="min-h-screen" data-testid="visit-prep-page">
      {/* Hero */}
      <section className="relative section-padding pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 to-background" />
        <div className="container-max relative">
          <div className="max-w-3xl">
            <p className="caption text-primary mb-4">Be Prepared</p>
            <h1 className="heading-1 text-foreground mb-6">Visit Preparation</h1>
            <p className="body-lg text-muted-foreground mb-8">
              Get AI-suggested questions to ask your hospice team based on your loved one's 
              condition and recent symptoms. Make every visit count.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding pt-8 bg-muted/30">
        <div className="container-max">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Disease Selection */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="glass-card border-0 shadow-soft">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-primary" />
                    Select Condition
                  </CardTitle>
                  <CardDescription>
                    Choose the primary diagnosis to see relevant questions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Select value={selectedDisease} onValueChange={setSelectedDisease}>
                    <SelectTrigger className="h-12" data-testid="disease-select">
                      <SelectValue placeholder="Select a condition..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(DISEASES).map(([key, disease]) => {
                        const Icon = disease.icon;
                        return (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${disease.color}`} />
                              {disease.name}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Personalized note */}
              {user ? (
                <Card className="glass-card border-0 shadow-soft bg-primary/5">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground mb-1">Personalized Questions</p>
                        <p className="text-xs text-muted-foreground">
                          Questions are also based on symptoms you've logged in your journal this week.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="glass-card border-0 shadow-soft">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <LogIn className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground mb-1">Sign in for personalized questions</p>
                        <p className="text-xs text-muted-foreground mb-2">
                          Get questions based on symptoms logged in your journal.
                        </p>
                        <Button size="sm" variant="outline" onClick={() => setShowAuthModal(true)}>
                          Sign In
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Questions */}
            <div className="lg:col-span-2">
              <Card className="glass-card border-0 shadow-soft">
                <CardHeader>
                  <CardTitle className="font-serif text-xl flex items-center gap-2">
                    {selectedDisease && <DiseaseIcon className={`w-5 h-5 ${DISEASES[selectedDisease]?.color}`} />}
                    Questions to Ask During Your Visit
                  </CardTitle>
                  {selectedDisease && (
                    <Badge variant="outline" className="w-fit">
                      {DISEASES[selectedDisease]?.name}
                    </Badge>
                  )}
                </CardHeader>
                <CardContent>
                  <Accordion type="multiple" defaultValue={["General"]} className="w-full">
                    {Object.entries(VISIT_QUESTIONS).map(([section, questions]) => (
                      <AccordionItem key={section} value={section}>
                        <AccordionTrigger className="hover:text-primary" data-testid={`accordion-${section}`}>
                          <div className="flex items-center gap-2">
                            <span className="font-serif">{section}</span>
                            <Badge variant="secondary" className="text-xs">{questions.length}</Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-3 pt-2">
                            {questions.map((question, index) => (
                              <div key={index} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                                <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-sm font-medium text-primary">
                                  {index + 1}
                                </span>
                                <p className="text-foreground">{question}</p>
                              </div>
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyQuestions(section, questions)}
                              className="mt-2"
                              data-testid={`copy-${section}`}
                            >
                              {copiedSection === section ? (
                                <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4 mr-2" />
                              )}
                              {copiedSection === section ? "Copied!" : "Copy Questions"}
                            </Button>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </div>
  );
}
